// FingerprintJS visitorId capture, fbp/fbc cookie reading, and PII collection
// from common form fields.

import FingerprintJS from '@fingerprintjs/fingerprintjs';
import type { AMInputs } from '../src/lib/hash';
import { readCookie, writeCookie } from './cookie';
import { getStore, updateStore, updateStoreSticky } from './store';

// Meta's _fbp/_fbc cookies have a 90-day TTL by default.
const META_COOKIE_TTL_SECONDS = 90 * 24 * 60 * 60;

const VISITOR_STORAGE_KEY = 'capi_visitor_id';
const VISITOR_COOKIE = 'capi_vid';

// Memoized: multiple beacon script tags on one page (e.g. SS global pageview
// + per-page confirmation) must share a single FingerprintJS computation.
let _visitorIdPromise: Promise<string> | null = null;

export function getVisitorId(): Promise<string> {
  if (_visitorIdPromise) return _visitorIdPromise;
  _visitorIdPromise = (async () => {
    const cached = readCookie(VISITOR_COOKIE) ?? localStorage.getItem(VISITOR_STORAGE_KEY);
    if (cached) return cached;
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const id = result.visitorId;
    try { localStorage.setItem(VISITOR_STORAGE_KEY, id); } catch { /* private mode */ }
    writeCookie(VISITOR_COOKIE, id, 60 * 60 * 24 * 365);
    return id;
  })();
  return _visitorIdPromise;
}

export function getFbp(): string | null {
  return readCookie('_fbp');
}

// _fbc is set by Meta from the fbclid URL param. If the cookie is absent but
// fbclid is on the URL, synthesize it per Meta's spec: fb.<subdomain_index>.<ts>.<fbclid>
export function getFbc(): string | null {
  const cookie = readCookie('_fbc');
  if (cookie) return cookie;
  const url = new URL(window.location.href);
  const fbclid = url.searchParams.get('fbclid');
  if (!fbclid) return null;
  const subdomainIndex = 1;
  return `fb.${subdomainIndex}.${Date.now()}.${fbclid}`;
}

export function hasFreshFbclid(): boolean {
  try {
    return !!new URL(window.location.href).searchParams.get('fbclid');
  } catch { return false; }
}

// Before Meta's pixel boots on this page, write the canonical _fbp/_fbc back
// to host-scoped cookies if they're missing. This is what makes the original
// click attribution flow through the entire funnel — Meta's pixel reads the
// cookies we just restored, so all subsequent fbq events (browser-side) and
// CAPI events (server-side) carry the same first-touch values.
//
// Cookies are written host-scoped (Domain unset) on purpose — that matches
// how Meta's own pixel sets them, avoiding shadow-cookie collisions. The
// cross-subdomain transport for these values is the root-domain `capi_store`
// cookie, which the beacon reads on every page and uses to repopulate.
export function restoreMetaCookies(): void {
  const store = getStore();
  const freshFbclid = hasFreshFbclid();

  if (store._fbp && !readCookie('_fbp')) {
    writeCookie('_fbp', store._fbp, META_COOKIE_TTL_SECONDS, { domain: null });
  }

  // _fbc: only restore the stored value if there's no fresh fbclid in the
  // URL. A fresh fbclid means a new ad click — let Meta's pixel set the
  // current click as the source-of-truth.
  if (store._fbc && !readCookie('_fbc') && !freshFbclid) {
    writeCookie('_fbc', store._fbc, META_COOKIE_TTL_SECONDS, { domain: null });
  }
}

// Capture _fbp/_fbc into the store with first-touch-sticky semantics.
// Once a value is in the store it stays — except _fbc gets overwritten when
// the user lands with a fresh fbclid on the URL (a new ad click trumps the
// previous canonical).
export function captureMetaSignals(): void {
  const fbp = getFbp();
  const fbc = getFbc();

  if (fbp) updateStoreSticky({ _fbp: fbp });

  if (fbc) {
    if (hasFreshFbclid()) {
      updateStore({ _fbc: fbc });
    } else {
      updateStoreSticky({ _fbc: fbc });
    }
  }
}

// Best-effort PII collection from forms / dataLayer / window globals.
// Add hooks for vendor-specific shapes here.
export function collectPii(): AMInputs {
  const out: AMInputs = {};

  const grab = (sel: string): string | undefined => {
    const el = document.querySelector<HTMLInputElement>(sel);
    return el?.value?.trim() || undefined;
  };

  out.em = out.em ?? grab('input[type="email"]');
  out.em = out.em ?? grab('input[name*="email" i]');
  out.ph = out.ph ?? grab('input[type="tel"]');
  out.ph = out.ph ?? grab('input[name*="phone" i]');
  out.fn = out.fn ?? grab('input[name*="first" i]');
  out.ln = out.ln ?? grab('input[name*="last" i]');

  // dataLayer convention (GTM)
  const dl = (window as unknown as { dataLayer?: Array<Record<string, unknown>> }).dataLayer;
  if (Array.isArray(dl)) {
    for (const entry of dl) {
      if (typeof entry?.email === 'string')      out.em = out.em ?? entry.email;
      if (typeof entry?.phone === 'string')      out.ph = out.ph ?? entry.phone;
      if (typeof entry?.first_name === 'string') out.fn = out.fn ?? entry.first_name;
      if (typeof entry?.last_name === 'string')  out.ln = out.ln ?? entry.last_name;
    }
  }

  return out;
}
